"use client";

// Public top navigation. Stays a small "use client" island because of
// the hamburger drawer state — the rest of the landing page is still
// server-rendered. Drawer auto-closes on link tap and on viewport
// resize past the 920px breakpoint so resizing a browser doesn't
// strand the menu open.

import Link from "next/link";
import { useEffect, useState } from "react";
import BrandMark from "./BrandMark";

const ArrowIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const MenuIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const LINKS: Array<{ href: string; label: string }> = [
  { href: "/#features", label: "ฟีเจอร์" },
  { href: "/#cases", label: "ลูกค้า" },
  // Mobile app download — hidden until launch. App is still in
  // internal testing; re-enable when the App Store / Play Store
  // listings are live.
  // { href: "/#download", label: "ดาวน์โหลด" },
  { href: "/articles", label: "บทความ" },
  { href: "/contact", label: "ติดต่อเรา" },
];

export default function PublicNav() {
  const [open, setOpen] = useState(false);

  // Close drawer on resize past the breakpoint — without this, opening
  // the drawer on mobile then rotating to landscape leaves it stuck open
  // behind the desktop nav.
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 920) setOpen(false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lock body scroll while drawer is open so the page beneath doesn't
  // scroll along with the user's swipe through the menu.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? "hidden" : prev;
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <header className="nav">
      <div className="wrap nav-inner">
        <Link href="/" className="logo" onClick={() => setOpen(false)}>
          <BrandMark size={28} />
          <span>
            <span className="flow">Flow</span>
            <span className="closer">Closer</span>
          </span>
        </Link>

        <nav className="nav-links" aria-label="Main">
          {LINKS.map((l) => (
            <Link key={l.href} href={l.href}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="nav-cta">
          <Link className="btn btn-ghost nav-signin" href="/auth/signin">
            เข้าสู่ระบบ
          </Link>
          <Link className="btn btn-primary nav-signup" href="/auth/signup">
            ทดลองฟรี <ArrowIcon />
          </Link>
          <button
            type="button"
            className="nav-burger"
            aria-label={open ? "ปิดเมนู" : "เปิดเมนู"}
            aria-expanded={open}
            aria-controls="public-mobile-menu"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile drawer — rendered always so transitions can run; visibility
          controlled via the data-open attribute (CSS handles the slide). */}
      <div
        id="public-mobile-menu"
        className="nav-drawer"
        data-open={open ? "true" : "false"}
        aria-hidden={!open}
      >
        <nav className="nav-drawer-links" aria-label="Mobile">
          {LINKS.map((l) => (
            <Link key={l.href} href={l.href} onClick={() => setOpen(false)}>
              {l.label}
            </Link>
          ))}
          <div className="nav-drawer-cta">
            <Link
              className="btn btn-ghost"
              href="/auth/signin"
              onClick={() => setOpen(false)}
            >
              เข้าสู่ระบบ
            </Link>
            <Link
              className="btn btn-primary"
              href="/auth/signup"
              onClick={() => setOpen(false)}
            >
              ทดลองฟรี 14 วัน <ArrowIcon />
            </Link>
          </div>
        </nav>
      </div>

      {/* Backdrop — taps close the drawer; absent when closed so it
          doesn't intercept clicks on the desktop nav. */}
      {open ? (
        <button
          type="button"
          className="nav-backdrop"
          aria-label="ปิดเมนู"
          onClick={() => setOpen(false)}
        />
      ) : null}
    </header>
  );
}
